import type { RumEvent, RumEventDomainContext } from '@datadog/browser-rum';
import { isLikelyAutomatedUserAgent } from '@/lib/bot-user-agent';

type DatadogRumApi = typeof import('@datadog/browser-rum').datadogRum;

let rumApi: DatadogRumApi | null = null;
let initPromise: Promise<DatadogRumApi | null> | null = null;

function isServiceWorkerRegisterRejectedError(message: string, stack?: string): boolean {
  const normalizedMessage = message.trim().toLowerCase();
  if (normalizedMessage !== 'rejected' && !normalizedMessage.includes('rejected')) {
    return false;
  }
  const normalizedStack = (stack ?? '').toLowerCase();
  return normalizedStack.includes('serviceworker') && normalizedStack.includes('register');
}

function isScriptErrorMessage(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('script error');
}

function safeReferrerHost(): string {
  if (typeof document === 'undefined' || !document.referrer) {
    return '';
  }
  try {
    return new URL(document.referrer).hostname;
  } catch {
    return '';
  }
}

/**
 * Drops known bot noise (service worker registration rejections) and enriches
 * cross-origin "Script error." events so Datadog issues are easier to triage.
 */
function rumBeforeSend(event: RumEvent, _context: RumEventDomainContext): boolean {
  if (event.type !== 'error') {
    return true;
  }

  const { message, stack } = event.error;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  if (
    isLikelyAutomatedUserAgent(ua) &&
    isServiceWorkerRegisterRejectedError(message, stack)
  ) {
    return false;
  }

  if (isScriptErrorMessage(message) && typeof window !== 'undefined') {
    event.context = {
      ...(event.context ?? {}),
      rum_script_error_debug: {
        pathname: window.location.pathname,
        referrer_host: safeReferrerHost(),
      },
    };
  }

  return true;
}

async function loadAndInitRum(): Promise<DatadogRumApi | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    console.warn('Datadog RUM not initialized: Missing applicationId or clientToken');
    return null;
  }

  const [{ datadogRum }, { reactPlugin }] = await Promise.all([
    import('@datadog/browser-rum'),
    import('@datadog/browser-rum-react'),
  ]);

  if (datadogRum.getInitConfiguration()) {
    rumApi = datadogRum;
    return datadogRum;
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com',
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'site',
    env: process.env.NEXT_PUBLIC_DATADOG_ENV || 'development',
    sessionSampleRate: Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE) || 0,
    sessionReplaySampleRate: Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE) || 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: rumBeforeSend,
    plugins: [reactPlugin()],
  });

  datadogRum.startSessionReplayRecording();
  rumApi = datadogRum;
  return datadogRum;
}

/**
 * Lazy-load Datadog RUM after idle so it stays out of the critical _app chunk.
 */
export const datadogInit = () => {
  if (typeof window === 'undefined') {
    return;
  }
  if (initPromise) {
    return;
  }

  const start = () => {
    initPromise = loadAndInitRum().catch((err) => {
      console.warn('[Datadog] Failed to initialize RUM', err);
      return null;
    });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(start, { timeout: 2500 });
  } else {
    setTimeout(start, 1500);
  }
};

/**
 * Facade used by hot paths that may fire before RUM finishes loading.
 * No-ops until init completes.
 */
export function datadogAddAction(
  name: string,
  context?: Record<string, unknown>,
): void {
  if (!rumApi?.getInitConfiguration()) {
    return;
  }
  rumApi.addAction(name, context);
}
